import { num, money, pct, marginColor } from "@/lib/format";
import QuoteBuilder from "@/components/workspace/QuoteBuilder";
import StackEngine from "@/components/workspace/StackEngine";
import CarrierComparison from "@/components/workspace/CarrierComparison";
import FinancialBreakdown from "@/components/workspace/FinancialBreakdown";
import RouteMap from "@/components/workspace/RouteMap";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Info, DollarSign, TrendingUp, Truck, Gauge, FilePlus2 } from "lucide-react";

function Hero({ label, value, sub, valueAccent, subAccent, icon: Icon, primary }) {
  return (
    <div className="bg-card p-4">
      <div className="flex items-center justify-between">
        <span className="label-caps">{label}</span>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
      </div>
      <div className={`metric-num mt-2 font-black leading-none ${primary ? "text-4xl" : "text-2xl"} ${valueAccent || ""}`}>{value}</div>
      {sub && <div className={`mt-1 text-xs font-semibold ${subAccent || "text-muted-foreground"}`}>{sub}</div>}
    </div>
  );
}

function EngCell({ label, value, sub, accent }) {
  return (
    <div className="min-w-[104px] flex-1 bg-card px-3 py-2.5">
      <div className="label-caps">{label}</div>
      <div className={`metric-num mt-0.5 text-base font-bold ${accent || ""}`}>
        {value}
        {sub && <span className="ml-1 text-[11px] font-normal text-muted-foreground">{sub}</span>}
      </div>
    </div>
  );
}

export default function CommandWorkspace({ view }) {
  const {
    form, setForm, products, result, financial, selectedRate, selectedCode,
    loading, manualRate, savedQuoteId, savedQuoteNumber, saving, warnings,
    onGetRates, onSelect, onManualRate, onApplyOverride, onClearOverride,
    onSave, onDuplicate, onCustomer, onDiagnostics, onExplain, onNewQuote,
  } = view;

  const s = result?.stack_result;
  const errors = warnings.filter((w) => w.level === "error").length;
  const confidence = selectedRate?.confidence_score ?? result?.confidence_score ?? 0;
  const confAccent =
    confidence >= 70 ? "text-[hsl(var(--success))]" : confidence >= 50 ? "text-[hsl(var(--warning))]" : "text-[hsl(var(--destructive))]";
  const marginAccent = financial ? marginColor(financial.margin_health) : "";

  return (
    <div className="space-y-5 animate-fade-up" data-testid="workspace-layout-command">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter">Freight Quote Workspace</h1>
          <p className="mt-1 text-sm text-muted-foreground">Command layout · deal outcomes first · sticky actions · live carrier comparison</p>
        </div>
        <Button data-testid="new-quote-button" variant="outline" onClick={onNewQuote} className="gap-1.5">
          <FilePlus2 className="h-4 w-4" /> New Quote
        </Button>
      </div>

      {/* Deal outcomes hero */}
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-border bg-border md:grid-cols-4" data-testid="deal-outcomes-hero">
        <Hero label="Recommended Sell" value={financial ? money(financial.sell_price) : "—"} sub={financial ? `Margin ${money(financial.margin_dollars)} · ${pct(financial.margin_pct)}` : "Calculate to price this shipment"} subAccent={marginAccent} icon={DollarSign} primary />
        <Hero label="Lowest Carrier Cost" value={result?.recommended ? money(result.recommended.total_carrier_cost) : "—"} sub={result?.recommended?.carrier_name} icon={Truck} />
        <Hero label="Est. Margin" value={financial ? pct(financial.margin_pct) : "—"} sub={financial ? money(financial.margin_dollars) : ""} valueAccent={marginAccent} subAccent={marginAccent} icon={TrendingUp} />
        <Hero label="Rate Confidence" value={result ? `${confidence}` : "—"} sub={selectedRate?.confidence_level || (errors ? `${errors} blocking issue${errors > 1 ? "s" : ""}` : "")} valueAccent={result ? confAccent : ""} icon={Gauge} />
      </div>

      {warnings.length > 0 && (
        <div className="space-y-1.5" data-testid="warnings-panel">
          {warnings.map((w, i) => (
            <div
              key={i}
              className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm ${
                w.level === "error"
                  ? "border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]"
                  : w.level === "warning"
                  ? "border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]"
                  : "border-border bg-card text-muted-foreground"
              }`}
            >
              {w.level === "info" ? <Info className="h-4 w-4 shrink-0" /> : <AlertTriangle className="h-4 w-4 shrink-0" />}
              {w.message}
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
        <div className="xl:col-span-4">
          <QuoteBuilder form={form} setForm={setForm} products={products} onGetRates={onGetRates} loading={loading} variant="command" />
        </div>
        <div className="space-y-5 xl:col-span-5">
          <RouteMap originZip={form.origin_zip} destZip={form.destination_zip} pickupDate={form.pickup_date} />
          {/* Engineering metrics strip */}
          <div className="flex flex-wrap gap-px overflow-hidden rounded-lg border border-border bg-border" data-testid="engineering-strip">
            <EngCell label="Cargo Weight" value={s ? num(s.total_cargo_weight) : "—"} sub={s ? "lb" : ""} />
            <EngCell label="Footprints" value={s ? num(s.total_stack_footprints) : "—"} sub={s ? `${s.full_stack_count}F` : ""} />
            <EngCell label="Tallest" value={s ? `${num(s.tallest_stack_height, 1)}"` : "—"} sub={s ? `/ ${num(s.height_limit)}"` : ""} />
            <EngCell label="Density" value={s?.full_stack ? num(s.full_stack.density, 2) : s?.last_stack ? num(s.last_stack.density, 2) : "—"} />
            <EngCell label="Freight Class" value={result?.calculated_class ? result.calculated_class : "—"} sub={result?.fak?.applied ? `FAK ${result.fak.fak_class}` : ""} accent="text-[hsl(var(--primary))]" />
          </div>
          <StackEngine result={result} />
          <CarrierComparison result={result} selectedCode={selectedCode} onSelect={onSelect} manualRate={manualRate} onManualRate={onManualRate} variant="command" />
          {manualRate && selectedCode === "MANUAL" && (
            <div className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2 text-xs text-muted-foreground">
              Manual rate entered. Recalculate to refresh comparison & financials.
              <button onClick={onGetRates} className="font-semibold text-[hsl(var(--primary))]">Recalculate</button>
            </div>
          )}
        </div>
        <div className="xl:col-span-3">
          <div className="xl:sticky xl:top-20">
            <FinancialBreakdown
              result={result}
              selectedRate={selectedRate}
              financial={financial}
              onApplyOverride={onApplyOverride}
              onClearOverride={onClearOverride}
              onSave={onSave}
              onDuplicate={onDuplicate}
              onCustomer={onCustomer}
              onDiagnostics={onDiagnostics}
              onExplain={onExplain}
              saving={saving}
              savedQuoteId={savedQuoteId}
              savedQuoteNumber={savedQuoteNumber}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
